import { SavedRouteAlert } from 'state/emailAlertsSlices';

export const DAY_NAMES = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
] as const;

export const DAY_PART_INTERVALS = {
  'Early AM': { startHour: 0, endHour: 6, displayTimeRange: '12am - 6am' },
  'Peak AM': { startHour: 6, endHour: 10, displayTimeRange: '6am - 10am' },
  'Mid Day': { startHour: 10, endHour: 15, displayTimeRange: '10am - 3pm' },
  'Peak PM': { startHour: 15, endHour: 19, displayTimeRange: '3pm - 7pm' },
  'Late PM': { startHour: 19, endHour: 23, displayTimeRange: '7pm - 12am' },
} as const;

export type DayPartLabel = keyof typeof DAY_PART_INTERVALS;

export enum MetricType {
  PERCENT_SLOWDOWN = 'PERCENT_SLOWDOWN',
  TRAVEL_TIME_TYPICAL = 'TRAVEL_TIME_TYPICAL',
  SPEED_TYPICAL = 'SPEED_TYPICAL',
  TRAVEL_TIME = 'TRAVEL_TIME',
  SPEED = 'SPEED',
}

export const METRIC_TYPE_DISPLAY_NAMES: Record<MetricType, string> = {
  [MetricType.PERCENT_SLOWDOWN]: 'Percent Slowdown',
  [MetricType.TRAVEL_TIME_TYPICAL]: 'Travel Time Typical',
  [MetricType.SPEED_TYPICAL]: 'Speed Typical',
  [MetricType.TRAVEL_TIME]: 'Travel Time',
  [MetricType.SPEED]: 'Speed',
};

/**
 Formats day type numbers (1-7) to day names Mon, Tue...
 */
export function formatDayTypes(dayTypes: number[]): string[] {
  return [...dayTypes]
    .sort((a, b) => a - b)
    .map((day) => DAY_NAMES[day - 1])
    .filter(Boolean);
}

/**
 * Formats day hours to readable time periods.
 * e.g. startHour * 100 + endHour (913 = 9 AM to 1 PM).
 */
export function formatDayHours(dayHours: number[]): string[] {
  return dayHours.map((hour) => {
    const startHour = Math.floor(hour / 100);
    const endHour = hour % 100;

    const match = (
      Object.entries(DAY_PART_INTERVALS) as [
        DayPartLabel,
        (typeof DAY_PART_INTERVALS)[DayPartLabel],
      ][]
    ).find(([, { startHour: s, endHour: e }]) => {
      if (s <= e) {
        return startHour >= s && startHour < e && endHour >= s && endHour <= e;
      }
      return startHour >= s && endHour <= e;
    });
    return match ? match[0] : '';
  });
}

/**
 * Checks if an hour value belongs to a specific day part.
 * startHour * 100 + endHour.
 */
export function hourBelongsToDayPart(
  hour: number,
  dayPartLabel: DayPartLabel,
): boolean {
  const startHour = Math.floor(hour / 100);
  const endHour = hour % 100;
  const { startHour: s, endHour: e } = DAY_PART_INTERVALS[dayPartLabel];
  if (s <= e) {
    return startHour >= s && startHour < e && endHour >= s && endHour <= e;
  }
  return startHour >= s && endHour <= e;
}

export function formatAlertName(
  metricType: string,
  metricCriteria: number,
): string {
  const metricTypeEnum = metricType as MetricType;
  const displayName = METRIC_TYPE_DISPLAY_NAMES[metricTypeEnum];

  if (displayName) {
    if (metricTypeEnum === MetricType.PERCENT_SLOWDOWN) {
      return `${Math.floor(metricCriteria * 100)}% Travel Time Increase`;
    }
    return `${displayName}: ${metricCriteria}`;
  }
  return `${metricType}: ${metricCriteria}`;
}

export function filterAlerts(
  alerts: SavedRouteAlert[],
  searchQuery: string,
): SavedRouteAlert[] {
  if (!searchQuery.trim()) return alerts;

  const query = searchQuery.toLowerCase();
  return alerts.filter((alert) => {
    const alertName = formatAlertName(
      alert.metric_type,
      alert.metric_criteria,
    ).toLowerCase();
    if (alertName.includes(query)) return true;

    const dayTypesStr = formatDayTypes(alert.schedule_day_types)
      .join(' ')
      .toLowerCase();
    if (dayTypesStr.includes(query)) return true;

    const dayPartsStr = formatDayHours(alert.schedule_day_hours)
      .join(' ')
      .toLowerCase();
    if (dayPartsStr.includes(query)) return true;

    const routeNames = alert.routes.map((r) => r.name.toLowerCase()).join(' ');
    if (routeNames.includes(query)) return true;

    const recipientEmails = alert.recipients.join(' ').toLowerCase();
    if (recipientEmails.includes(query)) return true;

    return false;
  });
}

export function filterWords(words: string[], searchQuery: string): string[] {
  if (!searchQuery.trim()) return words;

  const query = searchQuery.toLowerCase();
  return words.filter((word) => word.toLowerCase().includes(query));
}

// compare arrays (order-independent)
export const arraysEqual = (a: any[], b: any[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

// pluralize a word based on count
export function pluralize(count: number, word: string): string {
  return count !== 1 ? `${word}s` : word;
}

// convert route names to route id
export function routeNamesToIds(
  routeNames: string[],
  savedRoutes: Array<{ id: string; name: string }>,
): string[] {
  return routeNames.map((routeName) => {
    const route = savedRoutes.find((r) => r.name === routeName);
    return route?.id;
  });
}

/** /notifications/{log|manage|routes}/:projectSlug + ex edit alert */
export const NOTIFICATIONS_PATH_WITH_PROJECT =
  /^\/notifications\/(log|manage|routes)\/([^/]+)(.*)$/;

export function parseNotificationsPath(pathname: string): {
  section: 'log' | 'manage' | 'routes';
  projectSlug: string;
  rest: string;
} | null {
  const path = pathname.match(NOTIFICATIONS_PATH_WITH_PROJECT);
  if (!path) return null;
  return {
    section: path[1] as 'log' | 'manage' | 'routes',
    projectSlug: path[2],
    rest: path[3],
  };
}

/** Same notifications screen (section + subpath), different project. */
export function replaceNotificationsProjectInUrl(
  pathname: string,
  projectSlug: string,
): string | null {
  const parsedPath = parseNotificationsPath(pathname);
  if (!parsedPath) return null;
  return `/notifications/${parsedPath.section}/${projectSlug}${parsedPath.rest}`;
}

export const notificationLogUrl = (projectSlug: string) =>
  `/notifications/log/${projectSlug}`;

export const manageAlertsUrl = (projectSlug: string) =>
  `/notifications/manage/${projectSlug}`;

export const assignRoutesUrl = (projectSlug: string) =>
  `/notifications/routes/${projectSlug}`;

export const editAlertUrl = (projectSlug: string, alertId: string) =>
  `/notifications/manage/${projectSlug}/${alertId}/edit`;

export const viewAlertUrl = (projectSlug: string, alertId: string) =>
  `/notifications/manage/${projectSlug}/${alertId}`;

export const assignRouteUrl = (projectSlug: string, routeId: string) =>
  `/notifications/routes/${projectSlug}/${routeId}/assign`;

export const newAlertUrl = (projectSlug: string) =>
  `/notifications/manage/${projectSlug}/new`;
